/**
 * /questionnaire — Profile-completion wizard.
 *
 * Accessible to any authenticated user whose profile is not yet complete.
 * The _protected layout automatically redirects here from any other protected
 * page when isProfileComplete === false, and stops redirecting once it's true.
 */
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { QuestionnaireForm } from '@/features/questionnaire/questionnaire-form';

export const Route = createFileRoute('/_protected/questionnaire')({
  component: QuestionnairePage,
});

// ── Palette ───────────────────────────────────────────────────────────────────
const FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const CREAM  = 'hsl(var(--palette-cream,   40 100% 98%))';

function QuestionnairePage() {
  // Lock document scroll while the questionnaire is mounted. Both html and
  // body — Chrome/Firefox use html as the scrolling root in standards mode,
  // older browsers / quirks mode use body. Setting both is safe.
  React.useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, []);

  return (
    <main
      // `fixed` anchored below the navbar (h-16 = 4rem). Width fills via
      // left/right:0; height fills via top:4rem + bottom:0. No calc().
      className="fixed left-0 right-0 bottom-0 z-10 flex flex-col overflow-hidden"
      style={{
        top:             '4rem',
        backgroundColor: CREAM,
      }}
    >

      {/* ── Page header — fixed inside main; never scrolls ───────────────── */}
      <header
        className="shrink-0 py-6 sm:py-7 px-6"
        style={{ backgroundColor: FOREST }}
      >
        <div className="mx-auto max-w-lg text-center">
          <h1
            className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-1"
            style={{ color: CREAM }}
          >
            Complete your profile
          </h1>
          <p
            className="text-sm"
            style={{ color: 'hsl(var(--palette-cream, 40 100% 98%) / 0.80)' }}
          >
            Takes about 30 secs — needed to build your financial ecosystem.
          </p>
        </div>
      </header>

      {/* ── Form pane — the ONE place that scrolls when content overflows ── */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="mx-auto max-w-lg px-4 py-6 sm:py-8">
          <QuestionnaireForm />
        </div>
      </div>
    </main>
  );
}
